import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Density = "focus" | "context" | "deep";

interface DensityContextValue {
  density: Density;
  setDensity: (density: Density) => void;
}

const DensityContext = createContext<DensityContextValue | null>(null);

const STORAGE_KEY = "ares.density";

function readStoredDensity(): Density | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "focus" || raw === "context" || raw === "deep") return raw;
  return null;
}

interface DensityProviderProps {
  children: ReactNode;
  initialDensity?: Density;
}

/**
 * DensityProvider — V12-Density-Modi (Phase 2 Lock).
 *
 * Modi:
 *   - focus: kompakter Padding, kleinere Fonts (für Power-User-Cockpit)
 *   - context: Default (balance)
 *   - deep: expanded Padding, mehr Whitespace (für Reading-Sessions)
 *
 * Setzt `data-density` auf <html> für CSS-Variable-Overrides.
 * Components opt-in via `var(--density-padding-multiplier)` etc.
 *
 * W0.8.9 Skeleton — volle CSS-Variable-Implementation kommt in W1
 * wenn AppShell die Density durchregelt.
 */
export function DensityProvider({ children, initialDensity }: DensityProviderProps) {
  const [density, setDensityState] = useState<Density>(
    () => initialDensity ?? readStoredDensity() ?? "context",
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.density = density;
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const value = useMemo<DensityContextValue>(() => ({ density, setDensity }), [density, setDensity]);

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error("useDensity must be used within DensityProvider");
  return ctx;
}
